import React, { useState } from 'react';
import {
  Plane, Save, X, Pencil, Plus, Trash2, AlertTriangle, CheckCircle, CalendarDays,
  Link2 as LinkIcon,
} from 'lucide-react';

// Visa-grant detail panel — the immigration facts that matter for how a driver may
// work, shown/edited beneath the Visa Grant Notice document in both the driver
// portal (My Documents) and the office Compliance drawer. Display + capture only:
// nothing here enforces the work cap against actual hours yet.
//
// The whole object lives in the visa row's `fields.visaDetails` jsonb (no schema
// migration). Conditional sections are driven by the chosen category: study fields
// for Student / Temporary Graduate, partner fields for Partner. Kept in lock-step
// with sanitizeVisaDetails() in netlify/functions/driver-dms-sb.js.

export const VISA_CATEGORIES = ['Student', 'Temporary Graduate', 'Partner', 'Working Holiday', 'Skilled / Work', 'Permanent / Citizen', 'Other'];
export const COURSE_TYPES = ['ELICOS', 'Certificate IV', 'Diploma', 'Masters', 'Other'];
export const DEFAULT_WORK_LIMIT = '48 hours per fortnight';

// Condition codes as printed on a visa grant notice. 8104 vs 8105 is the tell-tale:
// 8104 is imposed on SECONDARY applicants (family members on someone else's visa),
// 8105 on the primary student — so 8104 identifies a dependant by itself.
const CONDITION_LABEL = {
  8101: 'no work',
  8104: 'work limitation — dependant',
  8105: 'work limitation — student',
  8201: 'max 3 months study',
  8202: 'meet course requirements',
  8208: 'no critical-technology study change',
  8501: 'maintain health insurance',
  8516: 'must maintain eligibility',
  8517: 'maintain schooling for dependants',
  8534: 'no further stay',
};
// Parse conditions whether stored as an array or still being typed as "8104, 8501".
export function condCodes(v) {
  const list = Array.isArray(v) ? v : String(v || '').split(/[,\s]+/);
  return [...new Set(list.map((c) => String(c).replace(/\D/g, '')).filter((c) => /^8\d{3}$/.test(c)))];
}

const showsStudy = (cat) => cat === 'Student' || cat === 'Temporary Graduate';
const showsPartner = (cat) => cat === 'Partner';
// Categories where someone can be a partner / dependent on another person's visa —
// a Student's family member (themselves a 500) or a Partner-visa secondary applicant.
const showsDependency = (cat) => cat === 'Student' || cat === 'Partner';

function fmtDate(d) {
  if (!d) return '';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}
function emptyDetails() {
  return {
    visaTypeLabel: '', subclass: '', vgn: '', trn: '', conditions: [], workRestricted: '', workLimit: '',
    courseType: '', courseLevel: '', courseName: '', institution: '', bridging: false, reviewDate: '',
    courseEndDate: '', courseEndEvidence: '',
    studyTerms: [], partner: { sponsorName: '', relationship: '', stage: '' },
    primary: { name: '', subclass: '', grantNumber: '', relationship: '', applicant: '', courseLevel: '', courseName: '', worksHere: false, login: '' },
  };
}
function normalise(d) {
  const s = { ...emptyDetails(), ...(d || {}) };
  s.partner = { sponsorName: '', relationship: '', stage: '', ...(d && d.partner) };
  s.primary = { name: '', subclass: '', grantNumber: '', relationship: '', applicant: '', courseLevel: '', courseName: '', worksHere: false, login: '', ...(d && d.primary) };
  s.studyTerms = Array.isArray(d && d.studyTerms) ? d.studyTerms.map((t) => ({ name: t.name || '', start: t.start || '', end: t.end || '' })) : [];
  s.bridging = !!s.bridging;
  return s;
}
export function hasVisaDetail(d) {
  if (!d) return false;
  const pr = d.primary || {};
  return !!(d.visaTypeLabel || d.subclass || d.vgn || d.trn || d.workRestricted || d.workLimit
    || d.courseType || d.courseLevel || d.courseName || d.institution || d.bridging
    || condCodes(d.conditions).length
    || (d.studyTerms && d.studyTerms.length) || d.courseEndDate
    || (d.partner && (d.partner.sponsorName || d.partner.relationship || d.partner.stage))
    || (pr.name || pr.subclass || pr.grantNumber || pr.relationship || pr.applicant || pr.courseLevel || pr.courseName));
}

// ── Read-only summary ──────────────────────────────────────────────────────────
function Summary({ d, studentHint }) {
  const cat = d.visaTypeLabel;
  const restr = d.workRestricted;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {(cat || d.subclass) && (
          <span style={{ color: 'var(--hp-text)', fontWeight: 700 }}>
            {cat || 'Visa'}{d.subclass ? ` · subclass ${d.subclass}` : ''}{d.primary && d.primary.applicant === 'secondary' ? ' · secondary' : ''}
          </span>
        )}
        {d.bridging && <span style={pill('#b8860b', 'rgba(201,168,76,0.14)')}>Bridging</span>}
        {restr === 'yes' && (
          <span style={pill('#b8860b', 'rgba(201,168,76,0.14)')}>
            <AlertTriangle size={11} /> Work-restricted — {d.workLimit || DEFAULT_WORK_LIMIT}
          </span>
        )}
        {restr === 'no' && (
          <span style={pill('var(--hp-green)', 'rgba(46,125,50,0.10)')}>
            <CheckCircle size={11} /> No work restrictions
          </span>
        )}
        {!restr && <span style={{ color: 'var(--hp-dim)' }}>Work restrictions not recorded</span>}
      </div>
      {condCodes(d.conditions).length > 0 && (
        <div>
          Conditions:{' '}
          <span style={{ color: 'var(--hp-text)' }}>
            {condCodes(d.conditions).map((c) => CONDITION_LABEL[c] ? `${c} (${CONDITION_LABEL[c]})` : c).join(' · ')}
          </span>
        </div>
      )}
      {(showsStudy(cat) || studentHint) && (d.courseType || d.courseLevel || d.courseName) && (
        <div>Course: <span style={{ color: 'var(--hp-text)' }}>{[d.courseName, d.courseType, d.courseLevel && (d.courseLevel === 'research' ? 'Research' : 'Coursework')].filter(Boolean).join(' · ')}</span></div>
      )}
      {(showsStudy(cat) || studentHint) && d.courseEndDate && (
        <div>
          Course end:{' '}
          <span style={{ color: 'var(--hp-text)', fontWeight: 700 }}>{fmtDate(d.courseEndDate)}</span>
          {d.courseEndEvidence && ` (per ${d.courseEndEvidence === 'completion_letter' ? 'completion letter' : 'CoE'})`}
          {d.courseEndDate < new Date().toISOString().slice(0, 10)
            && <span style={{ ...pill('var(--hp-green)', 'rgba(46,125,50,0.10)'), marginLeft: 6 }}><CheckCircle size={11} /> Completed — unlimited until the visa ends</span>}
        </div>
      )}
      {(showsStudy(cat) || studentHint) && d.studyTerms.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 700, color: 'var(--hp-dim)', marginBottom: 2 }}>
            <CalendarDays size={11} /> Study terms
          </div>
          {d.studyTerms.map((t, i) => (
            <div key={i} style={{ color: 'var(--hp-text)', paddingLeft: 16 }}>
              {t.name || `Term ${i + 1}`}{(t.start || t.end) ? `: ${fmtDate(t.start) || '?'} – ${fmtDate(t.end) || '?'}` : ''}
            </div>
          ))}
        </div>
      )}
      {showsPartner(cat) && (d.partner.sponsorName || d.partner.relationship || d.partner.stage) && (
        <div>
          Partner{d.partner.stage ? ` (${d.partner.stage})` : ''}:{' '}
          <span style={{ color: 'var(--hp-text)' }}>
            {[d.partner.sponsorName, d.partner.relationship].filter(Boolean).join(' · ') || '—'}
          </span>
        </div>
      )}
      {d.primary && d.primary.applicant === 'secondary' && (d.primary.name || d.primary.courseLevel) && (
        <div>
          Primary holder:{' '}
          <span style={{ color: 'var(--hp-text)' }}>
            {[d.primary.name, d.primary.courseName,
              d.primary.courseLevel && (d.primary.courseLevel === 'other' ? 'not Masters/Doctorate' : `${d.primary.courseLevel === 'doctorate' ? 'Doctorate' : 'Masters'} — no work cap`)
            ].filter(Boolean).join(' · ') || '—'}
          </span>
          {d.primary.worksHere && d.primary.login && (
            d.primary.linkVerified
              ? <span style={{ ...pill('var(--hp-green)', 'rgba(46,125,50,0.10)'), marginLeft: 6 }}><CheckCircle size={11} /> Link verified</span>
              : <span style={{ ...pill('#b8860b', 'rgba(201,168,76,0.14)'), marginLeft: 6 }}><AlertTriangle size={11} /> Link pending office verification</span>
          )}
        </div>
      )}
    </div>
  );
}
const pill = (color, bg) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 999, fontSize: '0.68rem', fontWeight: 800, color, background: bg, border: '1px solid transparent' });

// ── Editable form ──────────────────────────────────────────────────────────────
function Editor({ initial, onCancel, onSave, studentHint }) {
  const [d, setD] = useState(() => normalise(initial));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setPartner = (k, v) => setD((x) => ({ ...x, partner: { ...x.partner, [k]: v } }));
  const setPrimary = (k, v) => setD((x) => ({ ...x, primary: { ...x.primary, [k]: v } }));

  // Category is the pivot: pre-fill sensible restriction defaults so the office
  // isn't retyping "48 hours per fortnight" for every student. Always overridable.
  const onCategory = (cat) => setD((x) => {
    const next = { ...x, visaTypeLabel: cat };
    if (cat === 'Student' && !x.workRestricted) { next.workRestricted = 'yes'; if (!x.workLimit) next.workLimit = DEFAULT_WORK_LIMIT; }
    if (cat === 'Permanent / Citizen' && !x.workRestricted) next.workRestricted = 'no';
    return next;
  });
  const setRestricted = (v) => setD((x) => ({ ...x, workRestricted: v, workLimit: v === 'yes' && !x.workLimit ? DEFAULT_WORK_LIMIT : x.workLimit }));

  const addTerm = () => setD((x) => ({ ...x, studyTerms: [...x.studyTerms, { name: '', start: '', end: '' }].slice(0, 12) }));
  const setTerm = (i, k, v) => setD((x) => ({ ...x, studyTerms: x.studyTerms.map((t, j) => (j === i ? { ...t, [k]: v } : t)) }));
  const rmTerm = (i) => setD((x) => ({ ...x, studyTerms: x.studyTerms.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const res = await onSave(d);
      if (res && res.success === false) throw new Error(res.error || 'Could not save');
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const cat = d.visaTypeLabel;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--hp-border)' }}>
      {err && <div style={{ color: 'var(--hp-red)', fontSize: '0.76rem' }}>{err}</div>}

      <Field label="Visa type / category">
        <select value={cat} onChange={(e) => onCategory(e.target.value)} style={inputStyle}>
          <option value="">— Select —</option>
          {VISA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>

      <Field label="Subclass (number)">
        <input value={d.subclass} onChange={(e) => set('subclass', e.target.value)} placeholder="e.g. 500, 485, 820" style={inputStyle} />
      </Field>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 150px' }}>
          <Field label="Visa Grant Number (VGN)">
            <input value={d.vgn} onChange={(e) => set('vgn', e.target.value)} placeholder="e.g. 2009569567494" style={inputStyle} />
          </Field>
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <Field label="Transaction Ref (TRN)">
            <input value={d.trn} onChange={(e) => set('trn', e.target.value)} placeholder="TRN" style={inputStyle} />
          </Field>
        </div>
      </div>

      <Field label="Visa conditions (codes from the grant notice)">
        <input
          value={Array.isArray(d.conditions) ? d.conditions.join(', ') : d.conditions}
          onChange={(e) => set('conditions', e.target.value)}
          placeholder="e.g. 8104, 8501, 8516"
          style={inputStyle}
        />
      </Field>
      {condCodes(d.conditions).includes('8101') && (
        <div style={{ ...noteStyle, color: 'var(--hp-red)' }}><AlertTriangle size={11} /> 8101 — no work permitted on this visa.</div>
      )}
      {condCodes(d.conditions).includes('8104') && (
        <div style={noteStyle}>
          <AlertTriangle size={11} /> 8104 is the <b>dependant</b> work condition — this visa was granted as a family
          member on someone else’s visa (a primary student gets 8105). Capped at 48h/fortnight unless the primary
          holder studies a Masters or Doctorate.
        </div>
      )}
      {condCodes(d.conditions).includes('8104') && d.primary.applicant !== 'secondary' && (
        <div style={{ ...noteStyle, color: '#b8860b' }}>
          <AlertTriangle size={11} /> Mark “Partner / dependent” below and record the primary holder’s study level —
          a Masters / Doctorate primary lifts the cap.
        </div>
      )}

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: 'var(--hp-text)', cursor: 'pointer' }}>
        <input type="checkbox" checked={d.bridging} onChange={(e) => set('bridging', e.target.checked)} />
        This is a Bridging visa (no expiry — we’ll re-check in 3 months)
      </label>

      <Field label="Work restrictions">
        <div style={{ display: 'flex', gap: 8 }}>
          <Seg on={d.workRestricted === 'yes'} onClick={() => setRestricted('yes')} label="Yes — capped" />
          <Seg on={d.workRestricted === 'no'} onClick={() => setRestricted('no')} label="No — unrestricted" />
        </div>
      </Field>
      {d.workRestricted === 'yes' && (
        <Field label="Work limit">
          <input value={d.workLimit} onChange={(e) => set('workLimit', e.target.value)} placeholder={DEFAULT_WORK_LIMIT} style={inputStyle} />
        </Field>
      )}

      {/* Primary holder vs partner / dependent — the branch that decides whose study
          drives the work rights. Shown for Student & Partner visas. */}
      {showsDependency(cat) && (
        <Field label="Are you the primary visa holder, or a partner / dependent on someone else’s visa?">
          <div style={{ display: 'flex', gap: 8 }}>
            <Seg on={d.primary.applicant === 'primary'} onClick={() => setPrimary('applicant', 'primary')} label="Primary holder" />
            <Seg on={d.primary.applicant === 'secondary'} onClick={() => setPrimary('applicant', 'secondary')} label="Partner / dependent" />
          </div>
        </Field>
      )}

      {/* Study add-ons — the person's OWN course. Hidden for a dependent (they aren't
          the one enrolled — we capture the primary holder's study instead, below). */}
      {(showsStudy(cat) || studentHint) && d.primary.applicant !== 'secondary' && (
        <div style={sectionBox}>
          <div style={sectionTitle}>Study</div>
          <Field label="Course type">
            <select value={d.courseType} onChange={(e) => set('courseType', e.target.value)} style={inputStyle}>
              <option value="">— Select —</option>
              {COURSE_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Course level">
            <div style={{ display: 'flex', gap: 8 }}>
              <Seg on={d.courseLevel === 'coursework'} onClick={() => set('courseLevel', 'coursework')} label="Coursework" />
              <Seg on={d.courseLevel === 'research'} onClick={() => set('courseLevel', 'research')} label="Research (Masters/PhD)" />
            </div>
          </Field>
          {d.courseLevel === 'research'
            ? <div style={noteStyle}><CheckCircle size={11} /> Research Masters / Doctorate — unlimited work hours once the course has commenced.</div>
            : <div style={noteStyle}><AlertTriangle size={11} /> Coursework — capped at 48 hours per fortnight while in session.</div>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 150px' }}>
              <Field label="Course name">
                <input value={d.courseName} onChange={(e) => set('courseName', e.target.value)} placeholder="e.g. Master of IT (Research)" style={inputStyle} />
              </Field>
            </div>
            <div style={{ flex: '1 1 150px' }}>
              <Field label="Institution">
                <input value={d.institution} onChange={(e) => set('institution', e.target.value)} placeholder="College / university" style={inputStyle} />
              </Field>
            </div>
          </div>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', letterSpacing: '0.03em', margin: '4px 0 2px' }}>Study term timetable</div>
          {d.studyTerms.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-end', marginBottom: 6, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 100px' }}>
                <MiniLabel>Term</MiniLabel>
                <input value={t.name} onChange={(e) => setTerm(i, 'name', e.target.value)} placeholder="e.g. Term 1" style={inputStyle} />
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <MiniLabel>Start</MiniLabel>
                <input type="date" value={t.start} onChange={(e) => setTerm(i, 'start', e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <MiniLabel>End</MiniLabel>
                <input type="date" value={t.end} onChange={(e) => setTerm(i, 'end', e.target.value)} style={inputStyle} />
              </div>
              <button onClick={() => rmTerm(i)} title="Remove term" style={{ ...btn(false), padding: '8px 9px' }}><Trash2 size={13} /></button>
            </div>
          ))}
          {d.studyTerms.length < 12 && (
            <button onClick={addTerm} style={{ ...btn(false), alignSelf: 'flex-start' }}><Plus size={13} /> Add term</button>
          )}
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', letterSpacing: '0.03em', margin: '6px 0 2px' }}>Course end</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ flex: '1 1 150px' }}>
              <MiniLabel>End date (from the CoE / completion letter)</MiniLabel>
              <input type="date" value={d.courseEndDate} onChange={(e) => set('courseEndDate', e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: 8, flex: '1 1 200px' }}>
              <Seg on={d.courseEndEvidence === 'coe'} onClick={() => set('courseEndEvidence', 'coe')} label="CoE" />
              <Seg on={d.courseEndEvidence === 'completion_letter'} onClick={() => set('courseEndEvidence', 'completion_letter')} label="Completion letter" />
            </div>
          </div>
          <div style={noteStyle}><CheckCircle size={11} /> After this date the course is no longer in session: unlimited hours until the student visa ends. Clear it if they enrol in a further course.</div>
        </div>
      )}

      {/* Partner add-ons — sponsor details, partner visas only */}
      {showsPartner(cat) && (
        <div style={sectionBox}>
          <div style={sectionTitle}>Partner visa</div>
          <Field label="Sponsoring partner — name">
            <input value={d.partner.sponsorName} onChange={(e) => setPartner('sponsorName', e.target.value)} placeholder="Sponsor's full name" style={inputStyle} />
          </Field>
          <Field label="Relationship">
            <input value={d.partner.relationship} onChange={(e) => setPartner('relationship', e.target.value)} placeholder="e.g. Spouse, De facto" style={inputStyle} />
          </Field>
          <Field label="Stage">
            <input value={d.partner.stage} onChange={(e) => setPartner('stage', e.target.value)} placeholder="e.g. 820 (provisional), 801 (permanent)" style={inputStyle} />
          </Field>
        </div>
      )}

      {/* Primary visa holder — shown for ANY dependent (a Student's family member or a
          Partner secondary). Captures whose study drives the work rights + the link. */}
      {d.primary.applicant === 'secondary' && (
        <div style={{ ...sectionBox, borderStyle: 'dashed' }}>
          <div style={{ ...sectionTitle, color: 'var(--hp-dim)' }}>Primary visa holder</div>
          <div style={noteStyle}><AlertTriangle size={11} /> As a partner / dependent, your work rights follow the primary holder — we need their details, their enrolment (CoE + LoE) and what they’re studying.</div>
          <Field label="Primary holder — name">
            <input value={d.primary.name} onChange={(e) => setPrimary('name', e.target.value)} placeholder="Primary applicant's full name" style={inputStyle} />
          </Field>

          {/* Self-service link: if the primary also works here, tick this and we link
              your records so their CoE / LoE cover yours automatically. */}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '0.78rem', color: 'var(--hp-text)', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!d.primary.worksHere} onChange={(e) => setPrimary('worksHere', e.target.checked)} style={{ marginTop: 2 }} />
            <span>The primary holder <strong>also works here</strong> — link our records (their enrolment documents will cover mine).</span>
          </label>
          {d.primary.worksHere && d.primary.login && (
            <div style={{ ...noteStyle, color: 'var(--hp-green)' }}><LinkIcon size={11} /> Linked to {d.primary.name || d.primary.login} — their CoE / LoE flow into your documents.</div>
          )}
          {d.primary.worksHere && !d.primary.login && (
            <div style={noteStyle}><AlertTriangle size={11} /> We’ll match this name to a colleague when you save. If it doesn’t link, check the spelling matches how the office holds their name.</div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 120px' }}>
              <Field label="Their subclass"><input value={d.primary.subclass} onChange={(e) => setPrimary('subclass', e.target.value)} placeholder="e.g. 500, 482" style={inputStyle} /></Field>
            </div>
            <div style={{ flex: '1 1 120px' }}>
              <Field label="Their grant number"><input value={d.primary.grantNumber} onChange={(e) => setPrimary('grantNumber', e.target.value)} placeholder="VGN" style={inputStyle} /></Field>
            </div>
          </div>
          <Field label="Your relationship to them">
            <input value={d.primary.relationship} onChange={(e) => setPrimary('relationship', e.target.value)} placeholder="e.g. Spouse, Dependent" style={inputStyle} />
          </Field>

          {/* The primary's study level is the pivot for the dependent's work rights:
              a Masters (coursework OR research) or a Doctorate lifts the 48h cap. */}
          <Field label="What is the primary holder studying?">
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Seg on={d.primary.courseLevel === 'masters'} onClick={() => setPrimary('courseLevel', 'masters')} label="Masters" />
              <Seg on={d.primary.courseLevel === 'doctorate'} onClick={() => setPrimary('courseLevel', 'doctorate')} label="Doctorate" />
              <Seg on={d.primary.courseLevel === 'other'} onClick={() => setPrimary('courseLevel', 'other')} label="Other / lower" />
            </div>
          </Field>
          {(d.primary.courseLevel === 'masters' || d.primary.courseLevel === 'doctorate')
            ? <div style={noteStyle}><CheckCircle size={11} /> Primary is doing a Masters / Doctorate — you have <strong>no work restrictions</strong> (unlimited hours).</div>
            : d.primary.courseLevel === 'other'
              ? <div style={noteStyle}><AlertTriangle size={11} /> Primary is not on a Masters / Doctorate — you’re capped at 48 hours per fortnight while their course is in session.</div>
              : null}
          <Field label="Their course name (optional)">
            <input value={d.primary.courseName} onChange={(e) => setPrimary('courseName', e.target.value)} placeholder="e.g. Master of Business" style={inputStyle} />
          </Field>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
        <button onClick={save} disabled={busy} style={{ ...btn(true), opacity: busy ? 0.6 : 1 }}><Save size={14} /> {busy ? 'Saving…' : 'Save visa details'}</button>
        <button onClick={onCancel} disabled={busy} style={btn(false)}><X size={13} /> Cancel</button>
      </div>
    </div>
  );
}

// ── Public panel: view + toggle-to-edit, self-contained ──────────────────────────
// studentHint: VEVO says this is a student visa (subclass 500 / condition 8105) even if
// nobody has set the category — so the Study section (terms, course end) still shows.
export default function VisaDetailsPanel({ initial, onSave, defaultOpen = false, studentHint = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const d = normalise(initial);
  const filled = hasVisaDetail(d);

  const handleSave = async (details) => {
    const res = await onSave(details);
    if (!res || res.success !== false) setOpen(false);
    return res;
  };

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--hp-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: filled ? 8 : 0 }}>
        <Plane size={14} color="var(--hp-gold)" />
        <span style={{ fontWeight: 800, fontSize: '0.78rem', color: 'var(--hp-text)', letterSpacing: '0.02em' }}>Visa details</span>
        {!open && (
          <button onClick={() => setOpen(true)} style={{ ...btn(false), marginLeft: 'auto', padding: '5px 10px' }}>
            <Pencil size={12} /> {filled ? 'Edit' : 'Add details'}
          </button>
        )}
      </div>
      {!open && filled && <Summary d={d} studentHint={studentHint} />}
      {!open && !filled && <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 6 }}>Visa type, work restrictions, study or partner details — not recorded yet.</div>}
      {open && <Editor initial={initial} onCancel={() => setOpen(false)} onSave={handleSave} studentHint={studentHint} />}
    </div>
  );
}

// ── styling ──────────────────────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: '0.84rem', border: '1px solid var(--hp-border)',
  borderRadius: 'var(--radius)', background: 'var(--hp-bg)', color: 'var(--hp-text)', boxSizing: 'border-box',
};
const sectionBox = { border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 10, background: 'var(--hp-bg)', display: 'flex', flexDirection: 'column', gap: 8 };
const noteStyle = { display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--hp-dim)', lineHeight: 1.4 };
const sectionTitle = { fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--hp-gold)' };
function Field({ label, children }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', letterSpacing: '0.03em', marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}
function MiniLabel({ children }) {
  return <span style={{ display: 'block', fontSize: '0.64rem', fontWeight: 700, color: 'var(--hp-dim)', marginBottom: 3 }}>{children}</span>;
}
function Seg({ on, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: '8px 10px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', borderRadius: 'var(--radius)',
      border: `1px solid ${on ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
      background: on ? 'rgba(201,168,76,0.14)' : 'var(--hp-bg)',
      color: on ? 'var(--hp-text)' : 'var(--hp-dim)',
    }}>{label}</button>
  );
}
function btn(primary) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5, padding: '8px 12px', fontSize: '0.8rem', fontWeight: 700,
    borderRadius: 'var(--radius)', cursor: 'pointer',
    border: primary ? 'none' : '1px solid var(--hp-border)',
    background: primary ? 'var(--hp-gold)' : 'var(--hp-surface)',
    color: primary ? '#1a1a2e' : 'var(--hp-text)',
  };
}
